import { call, put, select } from 'redux-saga/effects'
import { callApi } from '@/shared/tools/apiSagaHelper'
import { selectAuthData, selectExtraRoutesAuthData } from '@/containers/Login/AuthSelectors'
import { showSnackbar, SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'
import { updateScheduledCallback } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import { selectScheduledCallbackToday } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import * as Sentry from '@sentry/react'

export const scheduleCallbackModuleActions = {
  CREATE_SCHEDULE_CALLBACK: 'CREATE_SCHEDULE_CALLBACK',
  LIST_SCHEDULE_CALLBACK: 'LIST_SCHEDULE_CALLBACK',
  LOAD_TODAY_CALLBACKS: 'LOAD_TODAY_CALLBACKS',
}

export function* createScheduleCallback(action) {
  try {
    yield put(updateScheduledCallback({ loading: true, success: false, error: null, message: null }))
    const { enqueue, payload, zohoContactId, clientId, version } = action
    const { country } = yield select(selectAuthData)
    const { scheduleCallbackUrl } = yield select(selectExtraRoutesAuthData)

    if (!scheduleCallbackUrl) {
      yield put(updateScheduledCallback({ loading: false, success: false, error: 'Missing URL' }))
      return
    }

    const body = {
      contact_zoho_id: zohoContactId,
      contact_first_name: payload.contactFirstName,
      contact_last_name: payload.contactLastName,
      contact_phone: payload.contactPhone,
      country,
      contact_email: payload.contactEmail,
      scheduled_date: payload.scheduledDate,
      scheduled_time: payload.scheduledTime,
      assigned_to_agent: payload.assignedToAgent,
      source: payload.source || 'hop',
      business_type: payload.businessType,
      client_id: clientId,
      version,
    }

    const result = yield call(callApi, {
      endpoint: scheduleCallbackUrl,
      method: 'POST',
      showErrorSnackbar: true,
      payload: body,
    })

    yield put(
      updateScheduledCallback({
        loading: false,
        success: true,
        message: result?.message || null,
        lastCreatedAt: Date.now(),
        googleCalendarUrl: result?.google_calendar_url || null,
      }),
    )

    yield put({
      type: scheduleCallbackModuleActions.LIST_SCHEDULE_CALLBACK,
      zohoContactId,
      businessType: payload.businessType,
    })

    const today = yield select(selectScheduledCallbackToday)
    if (today.isOpen) {
      yield put({ type: scheduleCallbackModuleActions.LOAD_TODAY_CALLBACKS })
    }

    yield call(showSnackbar, enqueue, {
      type: SNACKBAR_TYPES.SUCCESS,
      message: result?.message || 'Callback saved successfully',
    })
  } catch (error) {
    const status = error?.status || 'unknown'
    yield put(updateScheduledCallback({ loading: false, success: false, error }))
    yield call(showSnackbar, action.enqueue, {
      type: SNACKBAR_TYPES.ERROR,
      message:
        error?.responseData?.error?.message || error?.responseData?.message || error?.message || 'Callback save error',
    })
    if (status === 409) {
      yield put({
        type: scheduleCallbackModuleActions.LIST_SCHEDULE_CALLBACK,
        zohoContactId: action.zohoContactId,
        businessType: action.payload?.businessType,
      })
    }
    if (import.meta.env.PROD && status !== 422 && status !== 409) {
      Sentry.captureException(new Error('createScheduleCallback error'), { extra: { error } })
    }
  }
}

export function* loadScheduledCallbacks({ zohoContactId }) {
  try {
    if (!zohoContactId) return
    yield put(
      updateScheduledCallback({
        upcoming: { isLoading: true, error: null },
      }),
    )

    const { scheduleCallbackListUrl } = yield select(selectExtraRoutesAuthData)
    if (!scheduleCallbackListUrl) {
      yield put(
        updateScheduledCallback({
          upcoming: { isLoading: false, error: 'Missing URL' },
        }),
      )
      return
    }

    const { country } = yield select(selectAuthData)

    const params = new URLSearchParams({
      contact_zoho_id: String(zohoContactId),
      country: String(country || '').toLowerCase(),
    })

    const endpoint = `${scheduleCallbackListUrl}?${params.toString()}`
    const result = yield call(callApi, {
      endpoint,
      method: 'GET',
      showErrorSnackbar: true,
    })

    const items = Array.isArray(result?.data) ? result.data : []

    yield put(
      updateScheduledCallback({
        upcoming: {
          isLoading: false,
          items,
          loadedForZohoId: zohoContactId,
          error: null,
        },
      }),
    )

    if (items.length > 0) {
      const firstItem = items[0]
      const scheduleAtDateString = typeof firstItem.scheduledAt === 'string' ? firstItem.scheduledAt : ''
      const parts = scheduleAtDateString.split(' ')
      const dateStr = parts[0] || ''
      const timeStr = parts[1] || ''
      const [hh = '', mm = ''] = timeStr.split(':')

      const assignedRaw =
        firstItem && firstItem.assigned_to_agent !== undefined
          ? firstItem.assigned_to_agent
          : firstItem && firstItem.assignedToAgent !== undefined
            ? firstItem.assignedToAgent
            : null
      let assignedValue
      if (assignedRaw !== null) {
        const isSelf = assignedRaw === true || assignedRaw === 1 || assignedRaw === '1'
        assignedValue = isSelf ? 'self' : 'first'
      }

      if (dateStr && hh && mm) {
        yield put(
          updateScheduledCallback({
            form: {
              scheduledDate: dateStr,
              hour: String(hh),
              minutes: String(mm),
              ...(assignedValue ? { assigned: assignedValue } : {}),
            },
          }),
        )
      }
    }
  } catch (error) {
    yield put(
      updateScheduledCallback({
        upcoming: { isLoading: false, error },
      }),
    )
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('loadScheduledCallbacks error'), { extra: { error } })
    }
  }
}

export function* loadTodayCallbacks() {
  const today = yield select(selectScheduledCallbackToday)
  try {
    yield put(updateScheduledCallback({ today: { ...today, isLoading: true, error: null } }))

    const { scheduleCallbackTodayUrl } = yield select(selectExtraRoutesAuthData)
    if (!scheduleCallbackTodayUrl) {
      yield put(updateScheduledCallback({ today: { ...today, isLoading: false, error: 'Missing URL' } }))
      return
    }

    const result = yield call(callApi, { endpoint: scheduleCallbackTodayUrl, method: 'GET' })
    const items = Array.isArray(result?.data) ? result.data : []
    const current = yield select(selectScheduledCallbackToday)

    yield put(updateScheduledCallback({ today: { ...current, isLoading: false, items, error: null } }))
  } catch (error) {
    const current = yield select(selectScheduledCallbackToday)
    yield put(updateScheduledCallback({ today: { ...current, isLoading: false, error } }))
  }
}
